import { useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import {
  ConfirmationDialog,
  ConfirmationDialogAction,
  ConfirmationDialogCancel,
  ConfirmationDialogContent,
  ConfirmationDialogDescription,
  ConfirmationDialogFooter,
  ConfirmationDialogHeader,
  ConfirmationDialogOverlay,
  ConfirmationDialogPortal,
  ConfirmationDialogRoot,
  ConfirmationDialogTitle,
} from '@mint-lib/tw-components';

export type BulkConfirmationAction =
  | 'activate'
  | 'deactivate'
  | 'sendInvitation'
  | 'remove';

// Every action the bulk toolbar can emit. `editBulk` opens the role modal the rest go through the confirmation dialog.
export type BulkUserAction = BulkConfirmationAction | 'editBulk';

type Props = {
  action: BulkConfirmationAction;
  onClose: () => void;
  onConfirm: () => void;
};

type BulkActionContent = {
  title: string;
  body: string[];
  confirmLabel: string;
  color: 'primary' | 'warning';
  isWarning?: boolean;
};

const getBulkActionContent = (
  t: (value: string) => string,
): Record<BulkConfirmationAction, BulkActionContent> => ({
  activate: {
    title: t('Activate selected users?'),
    body: [
      t(
        'Only users with status "Deactivated" will be activated. Users with other statuses will not be affected.',
      ),
      t(
        'After activation, users can access the workspace again based on their permissions.',
      ),
    ],
    confirmLabel: t('Yes, activate'),
    color: 'primary',
  },
  deactivate: {
    title: t('Deactivate selected users?'),
    body: [
      t(
        'Only users with status "Active" will be deactivated. Users with other statuses will not be affected.',
      ),
      t(
        'After deactivation, users can no longer access the workspace based on their permissions.',
      ),
    ],
    confirmLabel: t('Yes, deactivate'),
    color: 'primary',
  },
  sendInvitation: {
    title: t('Send invitation to selected users?'),
    body: [
      t(
        'Only users with status "Pending" will receive the invitation. Users with other statuses will not be affected.',
      ),
    ],
    confirmLabel: t('Yes, send invitation'),
    color: 'primary',
  },
  remove: {
    title: t('Remove users from workspace?'),
    body: [
      t(
        'This action will remove these users from this workspace only. Their account will remain active and they may still access other workspaces.',
      ),
    ],
    confirmLabel: t('Yes, remove'),
    color: 'warning',
    isWarning: true,
  },
});

const BulkActionConfirmationDialog = ({
  action,
  onClose,
  onConfirm,
}: Props) => {
  const { t } = useTranslation('@myn/permissions');
  const content = getBulkActionContent(t)[action];

  return (
    <ConfirmationDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <ConfirmationDialogPortal>
        <ConfirmationDialogOverlay />
        <ConfirmationDialogRoot>
          <ConfirmationDialogHeader onClose={onClose}>
            {content.isWarning ? (
              <WarningAltFilled className="size-4 fill-warning-main" />
            ) : null}
            <ConfirmationDialogTitle>{content.title}</ConfirmationDialogTitle>
          </ConfirmationDialogHeader>
          <ConfirmationDialogContent>
            <ConfirmationDialogDescription className="flex flex-col gap-4">
              {content.body.map((paragraph) => (
                <span key={paragraph}>{paragraph}</span>
              ))}
            </ConfirmationDialogDescription>
          </ConfirmationDialogContent>
          <ConfirmationDialogFooter>
            <ConfirmationDialogCancel>{t('Cancel')}</ConfirmationDialogCancel>
            <ConfirmationDialogAction color={content.color} onClick={onConfirm}>
              {content.confirmLabel}
            </ConfirmationDialogAction>
          </ConfirmationDialogFooter>
        </ConfirmationDialogRoot>
      </ConfirmationDialogPortal>
    </ConfirmationDialog>
  );
};

export default BulkActionConfirmationDialog;
